import { Component, ElementRef, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PricingDto } from '../../../../api/models/pricing-dto';
import { SearchEntityResult } from '../../../../api/models/search-entity-result';
import { PricingService } from '../../../../api/services/pricing.service';
import { LocaleService } from '../../../../shared/locale/locale.service';

const ModelConcept = 'ModelConcept';
const ProductItem = 'ProductItem';
const SoftwareProduct = 'Product';

const NO_OP_FUNC = () => {
};

@Component({
  selector: 'app-search-entity',
  templateUrl: './search-entity.component.html',
  styleUrls: ['./search-entity.component.scss']
})
export class SearchEntityComponent implements OnInit {
  @Input()
  searchResultEntry!: SearchEntityResult;

  pricePrefix = '';
  price?: string;
  discountedPrice?: string;
  listPrice: string = 'N/A';
  currencyCode: string = 'N/A';
  discountedPriceExist = false;
  showPrice = false;
  isShowContactUs = false;
  isShowPurchaseUrl = false;
  ctaPassed = false;
  initDone = false;
  distributorExperience = false;
  extraInfoUnderTheTitle = '';
  isChinaOrTaiwan = false;

  constructor(
    private readonly el: ElementRef,
    private readonly localeService: LocaleService,
    private readonly pricingService: PricingService,
    public translateService: TranslateService
  ) {
  }

  private readonly FALLBACK_IMG_URL = 'https://ni.scene7.com/is/image/ni/EMR_NI_logo_152x116px';

  ngOnInit(): void {
    this.distributorExperience = this.searchResultEntry.distributorExperience || false;

    const currentLocale = this.localeService.getCurrentLocale().toLowerCase();
    this.isChinaOrTaiwan = currentLocale === 'zh-cn' || currentLocale === 'zhs' || currentLocale === 'zh-tw' || currentLocale === 'zht';

    this.initEntityB2B();
    this.setupExtraInfoUnderTheTitle();

    this.el.nativeElement.searchResultEntry = this.searchResultEntry;
    if (!this.searchResultEntry.image) {
      this.searchResultEntry.image = this.FALLBACK_IMG_URL;
    }
  }

  private initEntityB2B() {
    const entityType = this.searchResultEntry.entityType;
    switch (entityType) {
      case ModelConcept:
        this.verifyModelConceptDisplayLogic();
        break;
      case ProductItem:
        this.verifyProductItemDisplayLogic();
        break;
      case SoftwareProduct:
        this.verifySoftwareProductDisplayLogic();
        break;
    }
  }

  private verifyModelConceptDisplayLogic() {
    const { webOrderable, webSupportOnly, hidePrice } = this.searchResultEntry;

    if (this.isChinaOrTaiwan) {
      if (!webOrderable && webSupportOnly || (webOrderable && hidePrice)) {
        this.showContactUs();
        this.initDone = true;
      }

      if (webOrderable && !hidePrice) {
        this.showContactUs();
        this.checkEntityPrice();
      }
      return;
    }

    if (!webOrderable && webSupportOnly) {
      if (!this.distributorExperience) {
        this.showContactUs();
      }
      this.initDone = true;
      return;
    }

    if (webOrderable) {
      if (!this.distributorExperience && hidePrice) {
        this.showContactUs();
        this.initDone = true;
        return;
      }

      if (this.distributorExperience) {
        this.showPurchaseUrl();
        this.initDone = true;
        return;
      }

      if (!this.distributorExperience && !hidePrice) {
        this.checkEntityPrice();
      }
    }
  }

  private verifyProductItemDisplayLogic() {
    if (this.searchResultEntry.productType === 'Hardware' || this.searchResultEntry.productType === 'Bundle') {
      this.verifyProductItemHardwareDisplayLogic();
    } else {
      this.verifyProductItemSoftwareDisplayLogic();
    }
  }

  private verifyProductItemHardwareDisplayLogic() {
    const { webOrderable, webSupportOnly, hidePrice } = this.searchResultEntry;

    if (this.isChinaOrTaiwan) {
      if (!webOrderable && webSupportOnly || (webOrderable && hidePrice)) {
        this.showContactUs();
        this.initDone = true;
      }
      if (webOrderable && !hidePrice) {
        this.showContactUs();
        this.checkEntityPrice();
      }
      return;
    }

    if (!webOrderable && webSupportOnly) {
      if (!this.distributorExperience) {
        this.showContactUs();
      }
      this.initDone = true;
      return;
    }

    if (webOrderable) {
      if (!this.distributorExperience && hidePrice) {
        this.showContactUs();
        this.initDone = true;
        return;
      }

      if (this.distributorExperience) {
        this.showPurchaseUrl();
        this.initDone = true;
        return;
      }

      if (!this.distributorExperience && !hidePrice) {
        this.checkEntityPrice();
      }
    }
  }

  private verifyProductItemSoftwareDisplayLogic() {
    const { marketingExclusion, countrySalesRestriction, webSellable } = this.searchResultEntry;

    if (marketingExclusion || countrySalesRestriction) {
      this.handleCountrySalesRestriction();
      return;
    }

    if (!webSellable) {
      this.initDone = true;
      return;
    }

    this.checkEntityPrice();
  }

  private verifySoftwareProductDisplayLogic() {
    const { marketingExclusion, hidePrice, countrySalesRestriction } = this.searchResultEntry;

    if (countrySalesRestriction) {
      this.handleCountrySalesRestriction();
      return;
    }

    if (this.distributorExperience) {
      this.isShowPurchaseUrl = true;
      this.initDone = true;
      return;
    }

    if (marketingExclusion || hidePrice) {
      this.showContactUs();
      this.initDone = true;
    } else {
      this.checkEntityPrice();
    }
  }

  private handleCountrySalesRestriction() {
    if (this.distributorExperience) {
      this.isShowPurchaseUrl = true;
      this.isShowContactUs = false;
    } else {
      this.isShowPurchaseUrl = false;
      this.showContactUs();
    }

    this.initDone = true;
  }

  private checkEntityPrice() {
    const entityType = this.searchResultEntry.entityType;
    const entityId = this.getEntityId();
    if (entityId) {
      const parameters = {
        id: entityId,
      }

      switch (entityType) {
        case ProductItem:
          this.pricingService.getProductItemPrice(parameters).subscribe({
            next: resp => {
              if (this.searchResultEntry.productType === 'Hardware' || this.searchResultEntry.productType === 'Bundle') {
                this.updateProductItemHardwarePrice(resp);
              } else {
                this.updateProductItemSoftwarePrice(resp);
              }
            },
            error: err => this.handlePricingError(err)
          });
          break;
        case ModelConcept:
          this.pricePrefix = this.translateService.instant('startingFrom') + ' ';
          this.pricingService.getModelConceptPrice(parameters).subscribe({
            next: resp => this.updateStartingAtPrice(resp),
            error: err => this.handlePricingError(err)
          });
          break;
        case SoftwareProduct:
          this.pricePrefix = this.translateService.instant('startingFrom') + ' ';
          this.pricingService.getSoftwareProductPrice(parameters).subscribe({
            next: resp => this.updateStartingAtPrice(resp),
            error: err => this.handlePricingError(err)
          });
          break;
        default:
          this.initDone = true;
      }
    } else {
      this.initDone = true;
    }
  }

  private getEntityId(): string | undefined {
    switch (this.searchResultEntry.entityType) {
      case ProductItem:
        return this.searchResultEntry.partNumber;
      case ModelConcept:
      case SoftwareProduct:
        return this.searchResultEntry.pmdmId;
      default:
        return undefined;
    }
  }

  private updateProductItemHardwarePrice(pricing: PricingDto) {
    this.price = pricing.formattedPrice;
    this.discountedPrice = pricing.formattedDiscountedPrice;
    this.listPrice = pricing.listPrice ?? 'N/A';
    this.currencyCode = pricing.currencyCode ?? 'N/A';
    this.discountedPriceExist = (this.discountedPrice && this.discountedPrice !== this.price) || false;
    const listPrice = parseInt(this.listPrice ?? '') || 0;

    if (this.isChinaOrTaiwan && this.price && listPrice > 1) {
      this.showPrice = true;
      this.showContactUs();
      this.initDone = true;
      return;
    }

    if (this.price && listPrice > 1) {
      this.showPrice = true;
    }

    this.showPurchaseUrl();
    this.initDone = true;
  }

  private updateProductItemSoftwarePrice(pricing: PricingDto) {
    this.price = pricing.formattedPrice;
    this.discountedPrice = pricing.formattedDiscountedPrice;
    this.listPrice = pricing.listPrice ?? 'N/A';
    this.currencyCode = pricing.currencyCode ?? 'N/A';
    this.discountedPriceExist = (this.discountedPrice && this.discountedPrice !== this.price) || false;
    const hidePrice = this.searchResultEntry.hidePrice;
    const listPrice = parseInt(this.listPrice ?? '') || 0;
    const displayContactUs = this.searchResultEntry.showContactUs;

    if (this.distributorExperience) {
      if (this.isChinaOrTaiwan) {
        this.showContactUs();
      } else {
        this.showPurchaseUrl();
      }

      this.initDone = true;
      return;
    }

    if (this.price && !hidePrice && listPrice > 1) {
      this.showPrice = true;
      if (displayContactUs || this.isChinaOrTaiwan) {
        this.showContactUs();
      } else {
        this.showPurchaseUrl();
      }
    } else {
      this.showContactUs();
    }

    this.initDone = true;
  }

  private updateStartingAtPrice(pricing: PricingDto) {
    this.price = pricing.formattedPrice;
    this.discountedPrice = pricing.formattedDiscountedPrice;
    this.discountedPriceExist = (this.discountedPrice && this.discountedPrice !== this.price) || false;
    if (this.price) {
      this.showPrice = true;
      this.ctaPassed = true;
    } else {
      this.showContactUs();
    }

    this.initDone = true;
  }

  private handlePricingError(error: any) {
    this.showContactUs();
    this.initDone = true;
  }

  private showContactUs() {
    this.isShowContactUs = true;
  }

  private showPurchaseUrl() {
    this.isShowPurchaseUrl = true;
  }

  private setupExtraInfoUnderTheTitle() {
    if (this.searchResultEntry.hidePrice) {
      this.extraInfoUnderTheTitle = this.translateService.instant('contactUsForPrice');
    }
  }
}
